/**
 * Admin Portal Login Component
 * Allows admin to access group portals using existing admin credentials
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import {
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface AdminPortalLoginProps {
  onLogin?: (admin: any) => void;
}

export const AdminPortalLogin: React.FC<AdminPortalLoginProps> = ({ onLogin }) => {
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use existing admin credentials (same as admin dashboard)
      const adminCredentials = {
        username: 'admin',
        password: 'admin123'
      };

      if (username === adminCredentials.username && password === adminCredentials.password) {
        // Store admin session
        const adminData = {
          id: 'admin_001',
          username: 'admin',
          name: 'SightTrails Admin',
          email: 'admin@sighttrails.com',
          role: 'admin',
          permissions: ['view_groups', 'manage_groups', 'chat_with_clients', 'manage_logistics'],
          loginTime: new Date().toISOString()
        };

        localStorage.setItem('adminPortalAuth', JSON.stringify(adminData));
        onLogin?.(adminData);
        navigate('/admin/groups');
      } else {
        setError('Invalid username or password. Please use your admin dashboard credentials.');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
    } flex items-center justify-center p-4`}>
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border max-w-md w-full p-8 transition-all duration-300 ${
        actualTheme === 'dark'
          ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
          : 'bg-white/90 border-neutral-200/50'
      }`}>
        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300 ${
            actualTheme === 'dark' ? 'bg-primary-600' : 'bg-primary-500'
          }`}>
            <UserIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className={`text-3xl font-bold transition-colors duration-300 ${
            actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
          }`}>
            Admin Portal Access
          </h1>
          <p className={`mt-2 transition-colors duration-300 ${
            actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
          }`}>
            Access group portals and manage client interactions
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className={`block text-sm font-semibold mb-2 transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            }`}>
              Username
            </label>
            <div className="relative">
              <UserIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
              }`} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${
                  actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                }`}
                placeholder="Enter your username"
                required
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-semibold mb-2 transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            }`}>
              Password
            </label>
            <div className="relative">
              <LockClosedIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-400'
              }`} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full pl-10 pr-12 py-3 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${
                  actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                }`}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                  actualTheme === 'dark' 
                    ? 'text-slate-400 hover:text-slate-300' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className={`p-4 border rounded-modern transition-colors duration-300 ${
              actualTheme === 'dark'
                ? 'bg-red-900/20 border-red-800'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                <span className={`text-sm transition-colors duration-300 ${
                  actualTheme === 'dark' ? 'text-red-400' : 'text-red-600'
                }`}>{error}</span>
              </div>
            </div>
          )}

          <div className={`p-4 rounded-modern transition-colors duration-300 ${
            actualTheme === 'dark'
              ? 'bg-primary-900/20 border border-primary-800/30'
              : 'bg-primary-50 border border-primary-200'
          }`}>
            <h4 className={`font-medium mb-2 transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-primary-300' : 'text-primary-800'
            }`}>
              Admin Portal Features
            </h4>
            <ul className={`text-sm space-y-1 transition-colors duration-300 ${
              actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-700'
            }`}>
              <li>• Access all group portals</li>
              <li>• Chat with individual clients</li>
              <li>• Manage group logistics</li>
              <li>• Monitor group activities</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 font-semibold disabled:opacity-50 hover-magnify-sm modern-focus"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Logging in...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <ArrowRightIcon className="w-5 h-5 mr-2" />
                Access Admin Portal
              </div>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className={`text-sm transition-colors duration-300 ${
              actualTheme === 'dark' 
                ? 'text-slate-400 hover:text-slate-200' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};





